/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  MenuItem,
  Paper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import Select from "../../components/SearchableSelect";
import Box from "@mui/material/Box";
import {
  getDowntimeSummary,
  getScheduleSummary,
  getSelectedDaysLabel,
  notifyMinOptions,
} from "./utils";
import { tokens } from "../../theme";
import { getTimeZoneAbbreviation } from "../../utils/dateUtils";

const styles = {
  root: { width: "100%", display: "flex", flexDirection: "column" },
  header: (colors) => ({
    fontSize: "13px",
    fontWeight: 600,
    color: colors.grey[100],
    pb: "12px",
  }),
  // Each config is its own bordered card so an enabled block reads as a unit.
  configCard: (colors) => ({
    width: "100%",
    backgroundImage: "none",
    boxShadow: "none",
    backgroundColor: colors.primary[700],
    border: `1px solid ${colors.grey[800]}`,
    borderRadius: "10px",
    p: "13px 14px",
  }),
  cardTitle: (colors) => ({
    fontSize: "13px",
    fontWeight: 500,
    color: colors.grey[100],
  }),
  // Summarises the schedule so the card is readable without expanding it.
  cardSummary: (colors) => ({
    fontSize: "11.5px",
    color: colors.grey[400],
  }),
  cardHeaderRow: { minHeight: "30px" },
  cardBody: (colors) => ({
    mt: "13px",
    pt: "13px",
    borderTop: `1px solid ${colors.grey[900]}`,
  }),
  fieldLabel: (colors) => ({
    fontSize: "11.5px",
    color: colors.grey[400],
    width: "74px",
    flexShrink: 0,
  }),
  timeSelect: (colors) => ({
    "& .MuiOutlinedInput-input": {
      fontSize: "12.5px",
      fontWeight: 500,
      py: "5px",
      pr: "9px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[800] },
    "& .MuiSelect-icon": { display: "none" },
  }),
  timeZone: (colors) => ({ fontSize: "11px", color: colors.grey[400] }),
  // A filled track: the selected choice is a raised chip inside it.
  segmentedGroup: (colors) => ({
    display: "inline-flex",
    p: "2px",
    borderRadius: "7px",
    backgroundColor: colors.grey[900],
  }),
  list: { display: "flex", flexDirection: "column", gap: "12px" },
  optionPill: (colors, checked) => ({
    px: "11px",
    py: "5px",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "11.5px",
    fontWeight: checked ? 500 : 400,
    userSelect: "none",
    backgroundColor: checked ? colors.primary[700] : "transparent",
    boxShadow: checked ? "0 1px 2px rgba(0,0,0,.08)" : "none",
    color: checked ? colors.grey[100] : colors.grey[400],
  }),
  // Square day toggles for the custom repeat, independent of each other.
  dayPill: (colors, checked) => ({
    width: "26px",
    height: "26px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "7px",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: 500,
    userSelect: "none",
    border: `1px solid ${checked ? colors.blue[500] : colors.grey[800]}`,
    backgroundColor: checked ? colors.blue[500] : "transparent",
    color: checked ? "#fff" : colors.grey[400],
  }),
  daysSummary: (colors) => ({ fontSize: "11px", color: colors.grey[400] }),
  notifyPill: (colors, checked) => ({
    minWidth: "28px",
    textAlign: "center",
    px: "8px",
    py: "5px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: checked ? 500 : 400,
    userSelect: "none",
    border: `1px solid ${checked ? colors.blue[500] : colors.grey[800]}`,
    backgroundColor: checked ? colors.blue[500] : "transparent",
    color: checked ? "#fff" : colors.grey[400],
  }),
  // Explains the ordering between the two schedules without opening a tooltip.
  note: (colors) => ({
    mt: "2px",
    p: "9px 11px",
    borderRadius: "7px",
    backgroundColor: colors.blue[100],
    fontSize: "11px",
    lineHeight: 1.5,
    color: colors.blue[700],
  }),
  unit: (colors) => ({ fontSize: "11px", color: colors.grey[400] }),
};

const pillStyle = { day: styles.dayPill, notify: styles.notifyPill };

const repeatGroups = ["Everyday", "Weekdays", "Custom"];
const dayInitials = ["M", "T", "W", "T", "F", "S", "S"];

const OptionPill = ({ checked, label, onClick, variant, colors }) => (
  <Box
    onClick={onClick}
    sx={(pillStyle[variant] || styles.optionPill)(colors, checked)}
  >
    {label}
  </Box>
);

const HiberWakeupConfig = ({
  header,
  envId,
  enabled,
  toggleEnabled,
  schedule,
  updateSchedule,
  repeat,
  updateRepeat,
  notify,
  updateNotify,
  note,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  // The schedule is stored as 24h "HH:mm"; the picker works in 12h and
  // converts back on every change.
  const hour24 = Number(schedule?.split(":")[0] ?? 0) || 0;
  const minParsed = schedule?.split(":")[1] || "00";
  const hourParsed = (hour24 % 12 || 12).toString();
  const meridiemParsed = hour24 < 12 ? "AM" : "PM";

  const to24Hour = (hour12, meridiem) => {
    const hour = Number(hour12) % 12;
    return ((meridiem === "PM" ? hour + 12 : hour) % 24)
      .toString()
      .padStart(2, "0");
  };

  const updateScheduleHour = (hour) => {
    updateSchedule(`${to24Hour(hour, meridiemParsed)}:${minParsed}`);
  };
  const updateScheduleMin = (min) => {
    updateSchedule(`${to24Hour(hourParsed, meridiemParsed)}:${min}`);
  };
  const updateScheduleMeridiem = (meridiem) => {
    updateSchedule(`${to24Hour(hourParsed, meridiem)}:${minParsed}`);
  };

  const determineDayGroup = () => {
    if (repeat.slice(0, 5).every(Boolean)) {
      if (repeat[5] && repeat[6]) {
        return "Everyday";
      } else if (!repeat[5] && !repeat[6]) {
        return "Weekdays";
      }
    }
    return "Custom";
  };

  // Custom stays selected while the days are being picked, even if the picked
  // set momentarily matches Everyday or Weekdays.
  const [customPinned, setCustomPinned] = useState(false);
  const dayGroupSelected = customPinned ? "Custom" : determineDayGroup();

  useEffect(() => {
    if (determineDayGroup() === "Custom") setCustomPinned(true);
  }, [repeat]);

  const selectGroup = (group) => {
    if (group === "Custom") {
      setCustomPinned(true);
      return;
    }
    setCustomPinned(false);
    const nextRepeat = Array(7).fill(true);
    if (group === "Weekdays") {
      nextRepeat[5] = false;
      nextRepeat[6] = false;
    }
    updateRepeat(nextRepeat);
  };

  const selectDay = (idx) => {
    const prevDays = [...repeat];
    prevDays[idx] = !prevDays[idx];
    updateRepeat(prevDays);
  };

  const toggleNotifyMin = (idx) => {
    const currNotify = [...notify];
    currNotify[idx] = !currNotify[idx];
    updateNotify(currNotify);
  };

  return (
    <Paper elevation={0} sx={styles.configCard(colors)}>
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={styles.cardHeaderRow}
      >
        <Box>
          <Typography sx={styles.cardTitle(colors)}>{header}</Typography>
          <Typography sx={styles.cardSummary(colors)}>
            {getScheduleSummary(schedule, repeat)}
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={enabled}
          onChange={(e) => toggleEnabled(envId, e)}
        />
      </Stack>
      {enabled ? (
        <Stack spacing={1.5} sx={styles.cardBody(colors)}>
          <Stack direction="row" alignItems={"center"} spacing={1}>
            <Typography sx={styles.fieldLabel(colors)}>Time</Typography>
            <Select
              size="small"
              sx={styles.timeSelect(colors)}
              value={hourParsed}
              onChange={(e) => updateScheduleHour(e.target.value)}
            >
              {Array(12)
                .fill(0)
                .map((_, idx) => (idx + 1).toString())
                .map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
            </Select>
            <Typography sx={styles.unit(colors)}>:</Typography>
            <Select
              size="small"
              sx={styles.timeSelect(colors)}
              value={minParsed}
              onChange={(e) => updateScheduleMin(e.target.value)}
            >
              {["00", "15", "30", "45"].map((minute) => (
                <MenuItem key={minute} value={minute}>
                  {minute}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              sx={styles.timeSelect(colors)}
              value={meridiemParsed}
              onChange={(e) => updateScheduleMeridiem(e.target.value)}
            >
              {["AM", "PM"].map((meridiem) => (
                <MenuItem key={meridiem} value={meridiem}>
                  {meridiem}
                </MenuItem>
              ))}
            </Select>
            <Typography sx={styles.timeZone(colors)}>
              {getTimeZoneAbbreviation()}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems={"center"} spacing={1}>
            <Typography sx={styles.fieldLabel(colors)}>Repeats</Typography>
            <Stack
              direction="row"
              spacing={"2px"}
              sx={styles.segmentedGroup(colors)}
            >
              {repeatGroups.map((group) => (
                <OptionPill
                  key={group}
                  colors={colors}
                  checked={dayGroupSelected === group}
                  label={group}
                  onClick={() => selectGroup(group)}
                />
              ))}
            </Stack>
          </Stack>
          {dayGroupSelected === "Custom" ? (
            <Stack direction="row" alignItems={"flex-start"} spacing={1}>
              <Typography sx={styles.fieldLabel(colors)}>On days</Typography>
              <Stack spacing={"7px"}>
                <Stack direction="row" spacing={"5px"}>
                  {dayInitials.map((day, idx) => (
                    <OptionPill
                      key={`${day}-${idx}`}
                      colors={colors}
                      variant="day"
                      checked={repeat[idx]}
                      label={day}
                      onClick={() => selectDay(idx)}
                    />
                  ))}
                </Stack>
                <Typography sx={styles.daysSummary(colors)}>
                  {getSelectedDaysLabel(repeat)}
                </Typography>
              </Stack>
            </Stack>
          ) : null}
          <Stack direction="row" alignItems={"center"} spacing={1}>
            <Typography sx={styles.fieldLabel(colors)}>
              Notify before
            </Typography>
            <Stack direction="row" spacing={"5px"} alignItems={"center"}>
              {notifyMinOptions.map((minutes, idx) => (
                <OptionPill
                  key={minutes}
                  colors={colors}
                  variant="notify"
                  checked={notify[idx]}
                  label={minutes}
                  onClick={() => toggleNotifyMin(idx)}
                />
              ))}
              <Typography sx={styles.unit(colors)}>min</Typography>
            </Stack>
          </Stack>
          {note ? (
            <Typography sx={styles.note(colors)}>{note}</Typography>
          ) : null}
        </Stack>
      ) : null}
    </Paper>
  );
};

const ScheduledConfigs = ({ env, envOrig, setUpdates, onSetUpdates }) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const envId = env.id;

  // eslint-disable-next-line no-unused-vars
  const updateAutoDeleteHours = (event) => {
    setUpdates((prev) => {
      if (!prev[envId]) {
        prev[envId] = {};
      }
      prev[envId].autoDeleteHours = event.target.value;
      if (envOrig.autoDeleteHours.toString() === event.target.value) {
        delete prev[envId].autoDeleteHours;
      }
      return prev;
    });
    onSetUpdates();
  };

  // eslint-disable-next-line no-unused-vars
  const updateAutoDeleteEnabled = () => {
    setUpdates((prev) => {
      if (!prev[envId]?.autoDeleteEnabled) {
        prev[envId] = {
          ...(prev[envId] && prev[envId]),
          autoDeleteEnabled: env?.autoDeleteEnabled,
        };
      }
      prev[envId].autoDeleteEnabled = !prev[envId]?.autoDeleteEnabled;
      if (envOrig.autoDeleteEnabled === prev[envId].autoDeleteEnabled) {
        delete prev[envId].autoDeleteEnabled;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoHibernateEnabled = () => {
    setUpdates((prev) => {
      if (!prev[envId]?.autoHibernateEnabled) {
        prev[envId] = {
          ...(prev[envId] && prev[envId]),
          autoHibernateEnabled: env?.autoHibernateEnabled,
        };
      }
      prev[envId].autoHibernateEnabled = !prev[envId]?.autoHibernateEnabled;
      if (envOrig.autoHibernateEnabled === prev[envId].autoHibernateEnabled) {
        delete prev[envId].autoHibernateEnabled;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoHibernateSchedule = (time) => {
    setUpdates((prev) => {
      if (!prev[envId]) {
        prev[envId] = {};
      }
      prev[envId].autoHibernateSchedule = time;
      if (envOrig.autoHibernateSchedule === time) {
        delete prev[envId].autoHibernateSchedule;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoHibernateRepeat = (repeat) => {
    setUpdates((prev) => {
      if (!prev[envId]) {
        prev[envId] = {};
      }
      prev[envId].autoHibernateRepeat = repeat;
      if (envOrig.autoHibernateRepeat.toString() === repeat.toString()) {
        delete prev[envId].autoHibernateRepeat;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoHibernateNotify = (notify) => {
    setUpdates((prev) => {
      if (!prev[envId]) {
        prev[envId] = {};
      }
      prev[envId].autoHibernateNotify = notify;
      if (envOrig.autoHibernateNotify.toString() === notify.toString()) {
        delete prev[envId].autoHibernateNotify;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoWakeupEnabled = () => {
    setUpdates((prev) => {
      if (!prev[envId]?.autoWakeupEnabled) {
        prev[envId] = {
          ...(prev[envId] && prev[envId]),
          autoWakeupEnabled: env?.autoWakeupEnabled,
        };
      }
      prev[envId].autoWakeupEnabled = !prev[envId]?.autoWakeupEnabled;
      if (envOrig.autoWakeupEnabled === prev[envId].autoWakeupEnabled) {
        delete prev[envId].autoWakeupEnabled;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoWakeupSchedule = (time) => {
    setUpdates((prev) => {
      if (!prev[envId]) {
        prev[envId] = {};
      }
      prev[envId].autoWakeupSchedule = time;
      if (envOrig.autoWakeupSchedule === time) {
        delete prev[envId].autoWakeupSchedule;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoWakeupRepeat = (repeat) => {
    setUpdates((prev) => {
      if (!prev[envId]) {
        prev[envId] = {};
      }
      prev[envId].autoWakeupRepeat = repeat;
      if (envOrig.autoWakeupRepeat.toString() === repeat.toString()) {
        delete prev[envId].autoWakeupRepeat;
      }
      return prev;
    });
    onSetUpdates();
  };

  const updateAutoWakeupNotify = (notify) => {
    setUpdates((prev) => {
      if (!prev[envId]) {
        prev[envId] = {};
      }
      prev[envId].autoWakeupNotify = notify;
      if (envOrig.autoWakeupNotify.toString() === notify.toString()) {
        delete prev[envId].autoWakeupNotify;
      }
      return prev;
    });
    onSetUpdates();
  };

  return (
    <Box sx={styles.root}>
      <Typography sx={styles.header(colors)}>
        Scheduled configurations
      </Typography>
      <Box sx={styles.list}>
        <Paper elevation={0} sx={styles.configCard(colors)}>
          <Stack
            direction="row"
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={styles.cardHeaderRow}
          >
            <Box>
              <Typography sx={styles.cardTitle(colors)}>
                Auto delete DBs
              </Typography>
              <Typography sx={styles.cardSummary(colors)}>
                After {env.autoDeleteHours} hours idle
              </Typography>
            </Box>
            <Switch disabled size="small" checked={env.autoDeleteEnabled} />
          </Stack>
        </Paper>
        <HiberWakeupConfig
          header={"Auto hibernate"}
          envId={env.id}
          enabled={env.autoHibernateEnabled}
          toggleEnabled={updateAutoHibernateEnabled}
          schedule={env.autoHibernateSchedule}
          updateSchedule={updateAutoHibernateSchedule}
          repeat={env.autoHibernateRepeat}
          updateRepeat={updateAutoHibernateRepeat}
          notify={env.autoHibernateNotify}
          updateNotify={updateAutoHibernateNotify}
        />
        <HiberWakeupConfig
          header={"Auto wake up"}
          envId={env.id}
          enabled={env.autoWakeupEnabled}
          toggleEnabled={updateAutoWakeupEnabled}
          schedule={env.autoWakeupSchedule}
          updateSchedule={updateAutoWakeupSchedule}
          repeat={env.autoWakeupRepeat}
          updateRepeat={updateAutoWakeupRepeat}
          notify={env.autoWakeupNotify}
          updateNotify={updateAutoWakeupNotify}
          note={
            env.autoHibernateEnabled
              ? getDowntimeSummary(
                  env.autoHibernateSchedule,
                  env.autoWakeupSchedule
                )
              : ""
          }
        />
      </Box>
    </Box>
  );
};

export default ScheduledConfigs;
